import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import {
  IconBriefcase,
  IconNote,
  IconGrid,
  IconUser,
  IconList,
  IconCompass,
} from './Icons';
import { useAuth } from '../contexts/AuthContext';

/** Many parallel Supabase calls — 12s was too tight on cold start / slow networks. */
const FETCH_TIMEOUT_MS = 55000;

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// No-op lock avoids Navigator LockManager timeout when multiple tabs / Strict Mode remounts.
const noOpLock = async (_name, _acquireTimeout, fn) => fn();

// Dedicated client per page to avoid stale behavior after long background periods.
const pageSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    lock: noOpLock,
  },
});

const thisYearStart = `${new Date().getFullYear()}-01-01`;
const thisYearEnd = `${new Date().getFullYear()}-12-31`;

function formatMoney(n) {
  const num = Number(n);
  if (isNaN(num)) return '0.00';
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getDesignation(profile) {
  return profile?.Designation ?? profile?.designation ?? '';
}

function getDisplayName(profile, user) {
  return profile?.full_name?.trim() || user?.user_metadata?.full_name?.trim() || user?.email || 'User';
}

/** Title-case for display (e.g. "omar" → "Omar"). Skips emails. */
function formatDisplayName(name) {
  if (!name || typeof name !== 'string') return name;
  if (name.includes('@')) return name; // leave emails as-is
  return name
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export default function Dashboard() {
  const { user, profile } = useAuth();
  const designation = getDesignation(profile);
  const displayName = getDisplayName(profile, user);
  const displayNameFormatted = formatDisplayName(displayName);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [expensesThisYear, setExpensesThisYear] = useState([]);
  const [notesCount, setNotesCount] = useState(0);
  const [tendersCount, setTendersCount] = useState(0);
  const [procurementCount, setProcurementCount] = useState(0);
  const [filesCount, setFilesCount] = useState(0);

  const withTimeout = useCallback(async (promise, ms, label) => {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`${label} timed out after ${ms / 1000}s`)), ms)
      ),
    ]);
  }, []);

  const runWakeUp = useCallback(async () => {
    // Lightweight query to wake up PostgREST after backgrounding.
    return withTimeout(
      pageSupabase.from('profiles').select('id').limit(1),
      5000,
      'Wake-up'
    ).catch(() => {});
  }, [withTimeout]);

  const refreshSessionAndFetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Refresh auth session first (matches the pattern that prevented stale auth after resume).
      const { error: sessionErr } = await pageSupabase.auth.getSession();
      if (sessionErr) throw sessionErr;

      await runWakeUp();

      const fetchAllDashboard = () => {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
        );
        return Promise.race([
          Promise.all([
            pageSupabase
              .from('projects')
              .select('id, title, status, progress, deadline')
              .order('created_at', { ascending: false }),
            pageSupabase
              .from('invoices')
              .select(
                'id, total_invoiced, payment_received, due, status, doc_type, bill_date, due_date'
              )
              .order('created_at', { ascending: false }),
            pageSupabase.from('clients').select('id'),
            pageSupabase
              .from('tasks')
              .select('id, title, status, due_date, assignee_profile_id, project_id')
              .order('position', { ascending: true }),
            pageSupabase
              .from('profiles')
              .select('id')
              .order('full_name', { ascending: true, nullsFirst: false }),
            pageSupabase
              .from('expenses')
              .select('amount')
              .gte('expense_date', thisYearStart)
              .lte('expense_date', thisYearEnd),
            pageSupabase.from('notes').select('id', { count: 'exact', head: true }),
            pageSupabase.from('tenders').select('id', { count: 'exact', head: true }),
            pageSupabase.from('procurement_requests').select('id', { count: 'exact', head: true }),
            pageSupabase.from('fm_folders').select('id', { count: 'exact', head: true }),
            pageSupabase.from('fm_files').select('id', { count: 'exact', head: true }),
          ]),
          timeoutPromise,
        ]);
      };

      let batch;
      try {
        batch = await fetchAllDashboard();
      } catch (firstErr) {
        if (!String(firstErr?.message || '').includes('timed out')) throw firstErr;
        console.warn('[Dashboard] First fetch timed out, retrying after session refresh');
        await pageSupabase.auth.getSession();
        await runWakeUp();
        batch = await fetchAllDashboard();
      }

      const [p, inv, c, t, prof, expThis, notesRes, tendersRes, procurementRes, foldersRes, fmFilesRes] =
        batch;

      if (!p.error) setProjects(p.data ?? []);
      if (!inv.error) setInvoices(inv.data ?? []);
      if (!c.error) setClients(c.data ?? []);
      if (!t.error) setTasks(t.data ?? []);
      if (!prof.error) setProfiles(prof.data ?? []);
      if (!expThis.error) setExpensesThisYear(expThis.data ?? []);
      if (!notesRes?.error) setNotesCount(notesRes?.count ?? 0);
      if (!tendersRes?.error) setTendersCount(tendersRes?.count ?? 0);
      if (!procurementRes?.error) setProcurementCount(procurementRes?.count ?? 0);
      const foldersCount = !foldersRes?.error ? (foldersRes?.count ?? 0) : 0;
      const filesOnlyCount = !fmFilesRes?.error ? (fmFilesRes?.count ?? 0) : 0;
      setFilesCount(foldersCount + filesOnlyCount);
    } catch (e) {
      console.error('[Dashboard] fetch error', e);
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [runWakeUp]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await refreshSessionAndFetch();
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshSessionAndFetch]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      (async () => {
        try {
          await refreshSessionAndFetch();
        } catch (e) {
          console.error('[Dashboard] visible fetch error', e);
        }
      })();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  const invoiceList = useMemo(() => invoices.filter((i) => (i.doc_type || 'invoice') === 'invoice'), [invoices]);
  const totalDue = useMemo(() => invoiceList.reduce((s, i) => s + (Number(i.due) || 0), 0), [invoiceList]);
  const openProjects = useMemo(() => projects.filter((p) => (p.status || '').toLowerCase() === 'open'), [projects]);
  const myOpenTasks = useMemo(
    () => (user?.id ? tasks.filter((t) => t.assignee_profile_id === user.id && (t.status || 'todo') !== 'done') : []),
    [tasks, user?.id]
  );
  const expensesThisSum = useMemo(() => expensesThisYear.reduce((s, e) => s + (Number(e.amount) || 0), 0), [expensesThisYear]);

  if (loading) {
    return (
      <div className="dashboard dashboard-simple">
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>Loading…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard dashboard-simple">
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>Loading error: {error}</p>
        </div>
      </div>
    );
  }

  const linkCard = (to, icon, title, value, label) => (
    <Link to={to} className="dashboard-link-card card" style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="dashboard-link-icon">{icon}</div>
      <div className="dashboard-link-value">{value}</div>
      <div className="dashboard-link-title">{title}</div>
      <div className="dashboard-link-label">{label}</div>
    </Link>
  );

  return (
    <div className="dashboard dashboard-simple">
      <div className="dashboard-welcome-block">
        <p className="dashboard-welcome">
          Welcome our {designation ? <span className="dashboard-welcome-role">{designation} </span> : null}
          <span className="dashboard-welcome-name">{displayNameFormatted}</span>.
        </p>
      </div>
      <h2 className="dashboard-heading">Overview</h2>
      <div className="dashboard-kpis">
        {linkCard('/taskboard', <IconList />, 'My open tasks', myOpenTasks.length, 'View taskboard')}
        {linkCard('/sales/invoices', <IconCompass />, 'Amount due', `RO ${formatMoney(totalDue)}`, 'View invoices')}
        {linkCard('/projects', <IconBriefcase />, 'Open projects', openProjects.length, 'View projects')}
        {linkCard('/clients', <IconUser />, 'Clients', clients.length, 'View clients')}
      </div>

      <h2 className="dashboard-heading">Go to</h2>
      <div className="dashboard-links">
        {linkCard('/projects', <IconBriefcase />, 'Projects', projects.length, 'View projects')}
        {linkCard('/sales/invoices', <IconNote />, 'Invoices', invoiceList.length, 'View invoices')}
        {linkCard('/clients', <IconUser />, 'Clients', clients.length, 'View clients')}
        {linkCard('/taskboard', <IconList />, 'Taskboard', tasks.length, 'View tasks')}
        {linkCard('/team/members', <IconUser />, 'Team', profiles.length, 'View team')}
        {linkCard('/expenses', <IconGrid />, 'Expenses', `RO ${formatMoney(expensesThisSum)}`, 'View expenses')}
        {linkCard('/notes', <IconNote />, 'Notes', notesCount, 'View notes')}
        {linkCard('/tenders', <IconBriefcase />, 'Tenders', tendersCount, 'View tenders')}
        {linkCard('/procurement', <IconGrid />, 'Procurement', procurementCount, 'View procurement')}
        {linkCard('/files', <IconGrid />, 'Files', filesCount, 'View files')}
      </div>
    </div>
  );
}
